import STORE from '@/app/store/store';
import HELPER from '@/app/utils/HELPER';
import type {
  TypeExportData,
  AtomModel,
  Setting,
  Lib,
  Font,
  Data,
  TypeSettings,
} from '@/app/editor/iframe/atom/Atom.model';
import oldUtils from '@/app/utils/gfUtils.js';
import atom from '@/app/editor/iframe/atom';
import { getModuleData } from '@/app/editor/sidebar/utils/getModuleData';
import { initAce, copyToClipboard } from '@/app/utils/helpers';
import { AtomServiceExportData } from '@/app/editor/iframe/atom/atom-service/export/AtomServiceExportData';
import type { AtomExportInterface } from '@/app/editor/iframe/atom/atom-interface/AtomExportInterface';
import type { Observer, Subject } from '@/app/editor/iframe/atom/service/observer';
import { DataManager } from '@/app/editor/iframe/atom/service/DataManager';
import type { AtomRenderInterface } from '@/app/editor/iframe/atom/atom-interface/AtomRenderInterface';
import { BaseAtomRender } from '@/app/editor/iframe/atom/atom-service/render/BaseAtomRender';
import { autoCheckTemplate } from '@/app/utils/autoCheckTemplate';
import { getKeyFromModuleName } from '@/app/utils/moduleKeyName';
import { app } from '@/store';
import { base } from '@/app/editor/sidebar/constant';
import { addElement, removeElement } from '@/modules/gem-meter';
import emitter from '@/emitter';

declare let ace: any;

/**
 * Khởi tạo và kết nối các event của Atom
 */
export class Atom implements Observer {
  public $el: JQuery;
  public data = {
    snippet: undefined,
    atom: {} as AtomModel,
    notInitChildren: false,
  } as TypeSettings;
  public atomRender: AtomRenderInterface;
  public atomServiceExportData: AtomExportInterface;

  /* Editor */
  public $gfLeftSideBar: JQuery = jQuery('#gfLeftSideBar');
  public $gfResTool: JQuery = jQuery('#gfResTool');
  public $divContentHidden: JQuery = jQuery('#divContentHidden');
  /* Iframe */
  public $gfFrame: JQuery = jQuery('#gfFrame');
  public $gfFrameWrap: JQuery = jQuery('#gfFrameWrap');
  public $gryffEditor: JQuery = this.$gfFrame.contents().find('.gryffeditor').first();
  public $gryffBody: JQuery = this.$gfFrame.contents().find('body').first();
  public $divContentBk: JQuery = this.$gfFrame.contents().find('#divContentBk');
  public $gfTabs: JQuery;
  public _mdEditor =
    '<div id="md-editor" class="md-modal md-draggable">' +
    '<div class="md-content">' +
    '<div class="md-modal-handle">' +
    '<span class="md-title">' +
    '<span gf-tag="title">Code Editor</span>' +
    '<span gf-tag="type" gf-action="click:copyToClipboard"></span>' +
    '</span>' +
    '</div>' +
    '<div class="md-body code-panel">' +
    '<div class="section" gf-tabs="coding">' +
    '<div class="section-item disabled" gf-tab="template">Template</div>' +
    '<div class="section-item disabled" gf-tab="style">CSS</div>' +
    '<div class="section-item disabled" gf-tab="script">Script</div>' +
    '<div class="section-item disabled" gf-tab="data">Data</div>' +
    '</div>' +
    '<textarea id="templateCode" class="inptxt"></textarea>' +
    '<textarea id="styleCode" class="inptxt"></textarea>' +
    '<textarea id="scriptCode" class="inptxt"></textarea>' +
    '<textarea id="dataCode" class="inptxt"></textarea>' +
    '</div>' +
    '<div class="md-footer">' +
    '<button class="btnSave">Save</button>' +
    '<button class="btnCancel" class="cancel">Cancel</button>' +
    '</div>' +
    '</div>' +
    '<div class="md-controls">' +
    '<button class="btnClose"><i class="fa fa-times" aria-hidden="true"></i></button>' +
    '</div>' +
    '</div>';
  /** constructor
   * @param element DOM
   * @param options Seting
   */
  constructor(element: HTMLElement, options?: TypeSettings) {
    // init everthing
    this.$el = jQuery(element); // merge element to $el
    if (options) {
      // Merge options to data
      this.data = { ...this.data, ...options };
    }
    this.init();
  }

  /**
   * init
   */
  async init() {
    await this.initData();
    if ((window as any).__gpAutoFixTemplate) {
      autoCheckTemplate(this);
    }
    await this.render();
  }

  /**
   * implemnet obs to subcribe data change
   * @param subject obs
   */
  update(subject: Subject): void {
    const changeSetting = DataManager.getState('changeSetting', this.getId());
    if (changeSetting && changeSetting.type) {
      DataManager.setState('changeSetting', { type: undefined, data: undefined }, this.getId());
      switch (changeSetting.type) {
        case 'all': {
          this.render();
          break;
        }
        case 'css': {
          const singleStyle = changeSetting.data;
          DataManager.setDataSettingField(singleStyle, this.getId(), 'style');
          const data: AtomModel = DataManager.getAtomDataById(this.getId());
          this.updateCurrentSettingField('style', data.style);
          this.setStyle(data.style);
          break;
        }
      }
    }

    // State này được call từ class Dragable.
    // Lưu ý khi sử dụng
    const settingStateChange = DataManager.getState('setting', this.getId());
    if (settingStateChange && settingStateChange.changed) {
      DataManager.setState('setting', { changed: false }, this.getId());
      this.setSettings(settingStateChange.data);
      DataManager.setDataSettingField(settingStateChange.data, this.getId(), 'settings');
      DataManager.setTabSettingsDataField(
        {
          tab: settingStateChange.tab,
          drag: settingStateChange.drag,
          capture: settingStateChange.capture,
          forceOpen: settingStateChange.forceOpen,
        },
        this.getId(),
      );
    }
    if (DataManager.getState('cloneMe', this.getId()) == true) {
      DataManager.setState('cloneMe', false, this.getId());
      this.cloneMe();
    }
    if (DataManager.getState('remove', this.getId()) == true) {
      DataManager.setState('remove', false, this.getId());
      this.doRemoveMe();
      DataManager.removeObserver(this);
      DataManager.removeState(this.data.atom.id);
    }
    const changedSettingData = DataManager.getState('updateSettingData', this.getId());
    if (changedSettingData && changedSettingData.changed) {
      DataManager.setState('updateSettingData', { changed: false }, this.getId());
      DataManager.setDataSetting(changedSettingData.data, this.getId());
      this.updateInternalData();
    }
  }

  /**
   * Listen Refresh listen
   */
  listenRefresh() {
    STORE.events.listenOnlyOne(`refresh-${this.data.atom.id}`, () => {
      this.render();
    });
  }

  /**
   * [Init]
   * initData
   * Khởi tạo với các thông tin cơ bản
   * Nếu chưa có trong contentBk thì khởi tạo với snippet bên leftsidebar
   */
  initData(): void {
    const idBk = this.getIdBk();
    const label = this.getLabel();
    const dataKey = this.getModuleKey();
    const elmData = this.getDataBkSettingByKey(base._snippet, idBk);
    if (!elmData) {
      let $snippet;
      if (this.data.snippet) {
        $snippet = this.data.snippet;
      } else {
        $snippet = this.getDataSnippetByKey();
      }

      let icon = 'images/icons/default.png';
      const snippetIcon = $snippet.attr('data-icon');
      if (snippetIcon) {
        icon = snippetIcon;
      }
      let ver = '1';
      const snippetVer = $snippet.attr('data-ver');
      if (snippetVer) {
        ver = snippetVer;
      }

      let formField = '';
      const snippetFormField = $snippet.attr('data-form');
      if (snippetFormField) {
        formField = snippetFormField;
      }
      const snippet = $snippet.html();
      this.initWithSnippet(label, this.generateId(), icon, ver, snippet, formField);
    }

    this.data.atom = this.getDataAtom();
    // thêm dependencies cho Atom
    this.initAtomRender();
    this.initAtomExport();
    const elementId = this.getId();
    const merId = this.getIdNode();
    const dataEditorInfo: { [x: string]: any } = {};
    dataEditorInfo.id = elementId;
    dataEditorInfo.merId = merId;
    dataEditorInfo.key = dataKey;
    dataEditorInfo.label = this.getElementName();
    dataEditorInfo.data = {
      globalStyle: [],
    };
    app.$store.commit('editor/SET_DATA_SETTINGS', {
      id: elementId,
      value: JSON.stringify(dataEditorInfo),
    });
    if (dataKey) {
      addElement(merId, dataKey);
    }
  }

  /**
   * [Init]
   * initAtomExport
   */
  initAtomExport(): void {
    this.atomServiceExportData = new AtomServiceExportData(this);
  }

  /**
   * thêm AtomRender
   */
  initAtomRender() {
    this.atomRender = new BaseAtomRender(this);
  }

  /**
   * [Hook]
   * [Init]
   * getDataAtom
   * Khởi tạo một model data của atom
   * @returns AtomModel object
   */
  getDataAtom(): AtomModel {
    const data: AtomModel = {
      type: this.getType(),
      id: this.getId(),
      nodeId: this.getIdNode(),
      name: this.getLabel(),
      slug: this.getLabel(),
      groupName: this.getLabel(),
      settings: this.getSettings(),
      data: this.getDataChilds(),
      html: this.getTemplate(),
      class: this.getTargetClassSelector(),
      style: this.getStyle(),
      script: this.getScript(),
      libs: this.getLibs(),
      fonts: [] as Array<Font>,
      version: 1,
    };
    return data;
  }

  /**
   * [Init]
   * Nếu chưa có bk content thì tự động khởi tạo với snippet mặc định
   * TODO: hàm này mới chỉ đọc default data từ getyMySnippets, chưa support cho Element
   * @param label (Name)
   * @param atomIdNew (Id child)
   * @param icon (Icon atom)
   * @param ver (version)
   * @param snippet ()
   * @param formField (Thành phần trong Form)
   */
  initWithSnippet(
    label: string,
    atomIdNew: string,
    icon: string,
    ver: string,
    snippet: string,
    formField: string,
  ): void {
    const type = this.getType();
    switch (type) {
      case 'row': {
        this.$el
          .attr('id', 'r-' + atomIdNew)
          .addClass('gf_row')
          .attr('data-label', label)
          .attr('data-icon', icon)
          .attr('data-ver', ver)
          .attr('data-id', atomIdNew);
        break;
      }
      case 'element': {
        this.$el
          .attr('id', 'e-' + atomIdNew)
          .addClass('element-wrap')
          .attr('data-label', label)
          .attr('data-icon', icon)
          .attr('data-ver', ver)
          .attr('data-id', atomIdNew);
        break;
      }
      case 'module': {
        this.$el
          .attr('id', 'm-' + atomIdNew)
          .addClass('module-wrap')
          .attr('data-label', label)
          .attr('data-icon', icon)
          .attr('data-ver', ver)
          .attr('data-id', atomIdNew);
        break;
      }
    }

    if (formField) {
      this.$el.attr('data-form', formField);
    }
    const atomIdNewSlug = atomIdNew.toString().replace(/-/g, '');
    const $snippet = jQuery('<div/>').html(snippet);
    const $style = $snippet.find('.style');
    const $data = $snippet.find('.data');

    let style = $style.html();
    if (style != undefined) {
      switch (type) {
        case 'row': {
          style = style.replace(/{{(\S*\s+)?row-id(\S*\s+)?}}/g, atomIdNew);
          break;
        }
        case 'element': {
          style = style.replace(/{{(\S*\s+)?element-id(\S*\s+)?}}/g, atomIdNew);
          break;
        }
        case 'module': {
          style = style.replace(/{{(\S*\s+)?module-id(\S*\s+)?}}/g, atomIdNew);
          style = style.replace(/{{(\S*\s+)?module-id-slug(\S*\s+)?}}/g, atomIdNewSlug);
          break;
        }
      }
      style = HELPER.formatCSS(style);
      $style.html('');
      $style.html(style);
    }

    // check formfield mailchimp/contactform
    const $toModule = this.$el.closest('.module-wrap');
    if (formField && !this.$el.find('.elm').find('input, textarea, select').attr('name')) {
      if ($toModule.length && $toModule.attr('data-label') === 'Contact Form') {
        if (this.$el.find('.elm input[type="email"]').length) {
          this.$el.find('.elm input[type="email"]').attr('name', 'contact[email]').val('contact[email]');
          this.$el.find('.elm input[type="email"]').removeAttr('readonly');
          this.$el.find('.elm input[type="email"]').removeAttr('data-description');
        } else {
          this.$el
            .find('.elm')
            .find('input, textarea, select')
            .attr('name', 'contact[field-' + HELPER.MakeId() + ']');
        }
      } else {
        if (
          $toModule.length &&
          $toModule.attr('data-label') == 'Mailchimp' &&
          this.$el.find('.elm input[type="email"]').length
        ) {
          this.$el.find('.elm input[type="email"]').attr('name', 'email').val('email');
          this.$el.find('.elm input[type="email"]').removeAttr('readonly');
          this.$el.find('.elm input[type="email"]').removeAttr('data-description');
        } else {
          this.$el
            .find('.elm')
            .find('input, textarea, select, button')
            .attr('name', 'field-' + HELPER.MakeId());
        }
      }
    }
    let jsonData: any = {};
    if ($data.length > 0) {
      let jsonHtml = $data.text().trim();

      if (jsonHtml != undefined && jsonHtml != '') {
        switch (type) {
          case 'row': {
            jsonHtml = jsonHtml.replace(/{{(\S*\s+)?row-id(\S*\s+)?}}/g, atomIdNew);
            break;
          }
          case 'element': {
            jsonHtml = jsonHtml.replace(/{{(\S*\s+)?element-id(\S*\s+)?}}/g, atomIdNew);
            break;
          }
          case 'module': {
            jsonHtml = jsonHtml.replace(/{{(\S*\s+)?module-id(\S*\s+)?}}/g, atomIdNew);
            const atomIdSlug = atomIdNew.toString().replace(/-/g, '');
            jsonHtml = jsonHtml.replace(/{{(\S*\s+)?module-id-slug(\S*\s+)?}}/g, atomIdSlug);
            break;
          }
        }
      } else {
        jsonHtml = '{ "items": [] }';
      }
      try {
        jsonData = JSON.parse(jsonHtml);
      } catch (err) {
        console.log('Fail to parse jsonData', err);
      }

      // if (this.data.atom.settings.notInitChildren && jsonData.items) {
    }

    // tu dong them id cho cac items
    if (jsonData['items'] != undefined) {
      for (let i = 0; i < jsonData['items'].length; i++) {
        jsonData['items'][i]['id'] = 'i-' + atomIdNew + i;
      }
    }
    const jsonHtml = JSON.stringify(jsonData, null, '\t');
    $data.text(jsonHtml);
    //$template.remove(); // auto remove template
    HELPER.addContentBk(`<div id="${type}-${atomIdNew}">${$snippet.html()}</div>`);
    return;
  }

  /**
   * [Init]
   * render
   * Thay thế cho hàm refresh cũ - với mục tiêu refresh lại module khi setting thay đổi
   */
  async render(): Promise<void> {
    await this.atomRender.render();
  }

  /**
   * [Save data to Datasource]
   * @param style style value update
   * lấy style từ json_local + style trong snippets để gắn vào divContentBk
   */
  renderStyle(style?: string): void {
    this.atomRender.renderStyle(style);
  }

  /**
   * [Save data to Datasource]
   * Gắn script vào divContentBk
   * @param script : Chuỗi script cần chạy
   */
  renderScript(script?: string): void {
    this.atomRender.renderScript(script);
  }

  /**
   * [Settings]
   * Mở tab seting ở LeftSidebar thông qua Store
   * @param tab Tên tab
   * @param drag Được kéo ra hay được Click vào
   * @param capture Có lưu vào History hay không
   */
  openSettings(tab = 'Settings', drag = false, capture = true): void {
    DataManager.setState('openSetting', true, this.getId());
    DataManager.setTabSettingsDataField(
      {
        tab: tab,
        drag: drag,
        capture: capture,
        forceOpen: false,
      },
      this.getId(),
    );

    DataManager.getInstance().notify();
  }

  /**
   * Nhân bản một đối tượng (được gọi từ Toolbar)
   * @param removeOrigin ()
   * @param scrollToMe Sau khi nhân bản xong thì scroll đến đó hoặc không
   * @returns Đối tượng đã được nhân bản giống hệt bản gốc
   */
  cloneMe(removeOrigin = false, scrollToMe = true): JQuery | null {
    if (removeOrigin == undefined) {
      removeOrigin = false;
    }
    if (scrollToMe == undefined) {
      scrollToMe = true;
    }
    const _this = this;
    const data = this.export('clone');
    let html = data.html;
    let bkHtml = data.bkHtml;
    if (HELPER.isInsideTheList(this.$el)) {
      const $tmpHtml = jQuery('<div />').html(html);
      const newIdList: Array<string> = [];

      $tmpHtml.find('[data-label][data-id]').each(function () {
        const oldId = jQuery(this).attr('data-id') || '';
        const matchedString = new RegExp(oldId, 'g');

        let newId = HELPER.MakeIdInsideList(_this.$el, 'new');

        let increase = 0;
        while (jQuery.inArray(newId, newIdList) !== -1) {
          newId = HELPER.MakeIdInsideList(_this.$el, 'new', increase);
          increase++;
        }
        newIdList.push(newId);

        html = html.replace(matchedString, newId);
        bkHtml = bkHtml.replace(matchedString, newId);
      });
    } else {
      const rData = HELPER.replaceIds(data.html, data.bkHtml);
      html = rData.html;
      bkHtml = rData.bkHtml;
    }

    this.$el.after(html);
    HELPER.addContentBk(bkHtml);

    // new module
    let $newAtom = this.$el.next('.module-wrap');
    const type = this.getType();
    switch (type) {
      case 'row': {
        $newAtom = this.$el.next('.gf_row');
        break;
      }
      case 'element': {
        $newAtom = this.$el.next('.element-wrap');
        break;
      }
      case 'module': {
        $newAtom = this.$el.next('.module-wrap');
        break;
      }
    }
    atom.Init($newAtom);
    if (removeOrigin) {
      this.doRemoveMe();
    }

    /** Fix for Form case (old) Đoạn này cần xem lại */
    if ($newAtom.attr('data-form')) {
      const $formField = $newAtom.find('.elm').find('input, textarea, select');
      const oldName = $formField.attr('name');
      let newName = 'field-' + HELPER.MakeId();
      if (oldName) {
        if (oldName.indexOf('contact[') != -1) {
          newName = 'contact[' + newName + ']';
        }
      }
      $formField.attr('name', newName);
    }

    HELPER.flagListItemChange($newAtom, 'new');
    // detect element or module change
    const $itemContent = this.$el.closest('.item-content');
    HELPER.detectContentChange($itemContent);

    HELPER.refreshFrame();

    if ($newAtom && !HELPER.isInsideTheList($newAtom)) {
      if (!removeOrigin && scrollToMe) {
        const atomTop = $newAtom.offset()?.top || 0;
        const iframeTop = this.$gfFrame.offset()?.top || 0;
        const scrollTop = this.$gfFrame.contents().scrollTop() || 0;
        if (atomTop + iframeTop - scrollTop > window.innerHeight - 250) {
          this.$gryffEditor.parents('html, body').animate(
            {
              scrollTop: atomTop - 80,
            },
            800,
          );
        }
      }
    }
    setTimeout(() => {
      const $currentTargetElement = $newAtom;
      let position = {};
      if (this.$gryffEditor.data('gfhistory')) {
        position = this.$gryffEditor.data('gfhistory').getPosition($itemContent, $currentTargetElement);
      }
      const history = {
        id: $currentTargetElement.attr('id'),
        action: 'add',
        position: position,
      };

      this.$gryffEditor.trigger('history', history);
    }, 300);
    return $newAtom;
  }

  /**
   * fix function
   * @param exportType ..
   * @returns data
   */
  export(exportType?: string): TypeExportData {
    const data = this.exportMe(exportType);
    return data;
  }

  /**
   * exportMe
   * Tạo data để làm nguyên liệu cho Cloneme()
   * @param exportType export type
   * @returns TypeExportData
   */
  exportMe(exportType?: string): TypeExportData {
    // html có vỏ id
    const data = this.atomServiceExportData.export(exportType);
    return data;
  }

  /**
   * [Hook]
   * getLibsAtomInExport
   * @param $libsClone <script class ="libs"> lưu trong bkcontent
   * @returns mảng string các libs
   */
  getAllLibsAtomInExport($libsClone: JQuery): Array<Lib> {
    let libsClone = [] as Array<Lib>;
    if ($libsClone.html() != undefined && $libsClone.html().trim() != '') {
      let libJson: any = {};
      try {
        libJson = JSON.parse($libsClone.html().trim());
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log(err.message);
      }
      if (libJson['libs'] != undefined && libJson['libs'].length > 0) {
        libsClone = libJson['libs'];
      }
    }
    return libsClone;
  }

  /**
   * [Hook]
   * getLibsAtomInExport
   * @param $scriptClone <script class ="script"> lưu trong bkcontent
   * @returns string script
   */
  getAllScriptAtomInExport($scriptClone: JQuery): string {
    let scriptClone = '';
    if ($scriptClone.html() != undefined && $scriptClone.html().trim() != '') {
      scriptClone = $scriptClone.html();
      scriptClone = HELPER.formatCode(scriptClone);
    }
    return scriptClone;
  }

  /**
   * [Hook]
   * getLibsAtomInExport
   * @param $styleClone <script class ="script"> lưu trong bkcontent
   * @returns string style
   */
  getAllStyleAtomInExport($styleClone: JQuery): string {
    let styleClone = '';
    if ($styleClone.html() != undefined && $styleClone.html().trim() != '') {
      styleClone = $styleClone.html();
      styleClone = HELPER.formatCSS(styleClone);
    }
    return styleClone;
  }

  /**
   * [Hook]
   * getLibsAtomInExport
   * @param $styleClone <script class ="style"> lưu trong bkcontent
   * @returns mảng string các font
   */
  getAllFontsAtomInExport($styleClone: JQuery): Array<string> {
    let style = $styleClone.html();
    style = HELPER.compressHTML(style);
    const fonts = [];
    const fontRegex = /font-family.*?;/g;
    const fontsInStyle = style.match(fontRegex);
    if (fontsInStyle && fontsInStyle.length) {
      for (let i = 0; i < fontsInStyle.length; i++) {
        const fontName = HELPER.getFontNameFromString(fontsInStyle[i]);
        // also check these excludes in getFonts function
        if (jQuery.inArray(fontName, fonts) === -1 && jQuery.inArray(fontName, window._themeFonts) === -1) {
          fonts.push(fontName);
        }
      }
    }
    return fonts;
  }
  /**
   * [Destroy]
   * Xóa (remove) một đối tượng
   * @param isGotoTab0 focus vào tab Settings
   * @param historyFlag Lưu vào History hoặc không
   */
  doRemoveMe(isGotoTab0 = true, historyFlag = true): void {
    if (isGotoTab0 == undefined) {
      isGotoTab0 = true;
    }
    if (historyFlag == undefined) {
      historyFlag = true;
    }

    // update place holder
    const $placeholder = this.$el.next('.block-placeholder');
    if ($placeholder.length > 0) {
      $placeholder.remove();
    }

    const $itemContent = this.$el.closest('.item-content');
    const $rowEqualHeight = this.$el.closest('.gf_equal-height');

    if (historyFlag) {
      const $currentTargetAtom = this.$el;
      let position = {};
      if (this.$gryffEditor.data('gfhistory')) {
        position = this.$gryffEditor.data('gfhistory').getPosition($itemContent, $currentTargetAtom);
      }
      const data = this.export();
      const history = {
        id: this.getId(),
        action: 'delete',
        position: position,
        data: {
          html: data.html,
          bkHtml: data.bkHtml,
        },
      };
      this.$gryffEditor.trigger('history', history);
    }

    let flag = false;
    if (!HELPER.isInsideTheList(this.$el)) {
      // Không phải trong list
      this.removeAllChilds();

      // remove
      this.removeDataBkSettings();
      this.$el.remove();
    } else {
      // là trong list
      HELPER.flagListItemChange(this.$el, 'delete');
      flag = true;

      // remove
      // Nếu nằm trong list thì sẽ có cơ chết xóa riêng cho các atom
    }

    if ($rowEqualHeight.length > 0) {
      HELPER.refreshEqualHeight();
    }

    // detect element or module change
    HELPER.detectContentChange($itemContent);
    HELPER.refreshFrame();

    if (!flag) {
      // Xóa hết các dom có cùng id trên trang
      const type = this.getType();
      switch (type) {
        case 'row': {
          while (this.$gryffBody.find(this.getIdNode()).length > 0) {
            this.$gryffBody.find(this.getIdNode()).data('gfrow').doRemoveMe();
          }
          break;
        }
        case 'element': {
          while (this.$gryffBody.find(this.getIdNode()).length > 0) {
            this.$gryffBody.find(this.getIdNode()).data('gfelement').doRemoveMe();
          }
          break;
        }
        case 'module': {
          while (this.$gryffBody.find(this.getIdNode()).length > 0) {
            this.$gryffBody.find(this.getIdNode()).data('gfmodule').doRemoveMe();
          }
          break;
        }
      }
    }

    //   go back to tab 0
    if (isGotoTab0) {
      this.$gfLeftSideBar.data('gfleftsidebar').gotoTab(0);
    }

    // TODO: Hook support remove các thành phần phụ trợ cho meta hoặc popup
    return;
  }

  /**
   * [Destroy]
   * Xóa tất cả các thành phần bên trong Atom
   */
  removeAllChilds(): void {
    this.removeAllChildsInStore();
    return;
  }

  /**
   * removeAllChildsInStore
   */
  removeAllChildsInStore(): void {
    const $rows = this.$el.find('.gf_row');
    for (let k = 0; k < $rows.length; k++) {
      const rowId = $rows.eq(k).attr('data-id');
      this.removeChildDataBkSetting('row', rowId);
      const $style = this.get$StyleContainer().find('#' + base._style + '-' + rowId);
      $style.remove();
      const $script = this.get$ScriptContainer().find('#' + base._script + '-' + rowId);
      $script.remove();
    }
    const $cols = this.$el.find('.gf_column');
    for (let k = 0; k < $cols.length; k++) {
      const colId = $cols.eq(k).attr('data-id');
      this.removeChildDataBkSetting('column', colId);
      const $style = this.get$StyleContainer().find('#' + base._style + '-' + colId);
      $style.remove();
      const $script = this.get$ScriptContainer().find('#' + base._script + '-' + colId);
      $script.remove();
    }
    const $modules = this.$el.find('.module-wrap');
    for (let k = 0; k < $modules.length; k++) {
      const moduleId = $modules.eq(k).attr('data-id');
      this.removeChildDataBkSetting('module', moduleId);
      const $style = this.get$StyleContainer().find('#' + base._style + '-' + moduleId);
      $style.remove();
      const $script = this.get$ScriptContainer().find('#' + base._script + '-' + moduleId);
      $script.remove();
    }
    const $elems = this.$el.find('.element-wrap');
    for (let k = 0; k < $elems.length; k++) {
      const elementId = $elems.eq(k).attr('data-id');
      this.removeChildDataBkSetting('element', elementId);
      const $style = this.get$StyleContainer().find('#' + base._style + '-' + elementId);
      $style.remove();
      const $script = this.get$ScriptContainer().find('#' + base._script + '-' + elementId);
      $script.remove();
    }
    return;
  }

  /**
   * Lấy Label (Name)
   * @returns Name
   */
  getLabel(): string {
    return this.$el.attr('data-label') || '';
  }

  /**
   * Lấy Icon (Name class)
   * @returns Icon
   */
  getIcon(): string {
    return this.$el.attr('data-icon') || '';
  }

  /**
   * [Get data from Datasource]
   * Lấy data setting từ divContentBk
   * @returns Json object Fields
   */
  getSettings(): Array<Setting> {
    let fields = [] as Array<Setting>;
    try {
      const settingsTmp = this.getDataBkSettingByKey(base._settings);
      if (settingsTmp) {
        fields = JSON.parse(settingsTmp);
      }
      // eslint-disable-next-line no-empty
    } catch (err) {}
    fields = this.formatSettingAtom(fields);
    return fields;
  }

  /**
   * [Save data to Datasource]
   * Gắn setting data vào divContentBk
   * @param settings setting data
   */
  setSettings(settings: Array<Setting>): void {
    const txtSettings = JSON.stringify(settings);
    try {
      this.setDataBkSettingByKey(txtSettings, base._settings);
      // [Update local_json]
      this.updateCurrentSettingField('settings', settings);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(e);
    }
    return;
  }

  /**
   * [Hook]
   * @param settings ()
   * @returns settings data
   */
  formatSettingAtom(settings: Array<Setting>): Array<Setting> {
    return settings;
  }

  /**
   * [Hook]
   * [Get data from Datasource]
   * getDataChilds
   * Lấy data Child từ divContentBk
   * @returns Array<Data>
   */
  getDataChilds(): Data {
    let data = this.getDataBkSettingByKey(base._data);
    // Fix: Nếu data không có giá trị thì gán mặc định
    if (!data) {
      data = '{"items": []}';
    }
    const replaces = [];
    const reg = new RegExp(/font-family(.*?;)/g);
    let result;
    while ((result = reg.exec(data)) !== null) {
      const font = result[0].replace(/\\"/g, '"');
      replaces.push(font);
    }
    for (let i = 0; i < replaces.length; i++) {
      const oldFont = replaces[i];
      const newFont = oldFont.replace(/"/g, '');
      data = data.replace(oldFont, newFont);
    }
    let dataChilds: Data = { items: [] };
    try {
      dataChilds = JSON.parse(data);
    } catch (err) {
      console.log('Fail to parse string to Json', err);
    }
    return dataChilds;
  }

  /**
   * TODO: cập nhật code từ hàm detectModuleChange
   * Set lại biến local theo content bk
   * @param dataChilds Array<Data>
   */
  setDataChilds(dataChilds: AtomModel['data']): void {
    this.data.atom.data = dataChilds;
  }

  /**
   * [Get data from Datasource]
   * Lấy style từ divContentBk
   * @returns CSS
   */
  getStyle(): string {
    let style = this.getDataBkSettingByKey(base._style);
    if (!style) {
      const $styleContainer = this.get$StyleContainer().find(`#${base._style}-${this.getId()}`);
      style = $styleContainer.find('.style').html() ? $styleContainer.find('.style').html() : '';
    }
    return HELPER.formatCSS(style);
  }

  /**
   * [Save data to Datasource]
   * Gắn style vào divContentBk
   * @param style chuỗi Style
   */
  setStyle(style: string): void {
    this.setDataBkSettingByKey(style, base._style);
    this.data.atom.style = style;
    this.renderStyle(style);
    return;
  }

  /**
   * [Get data from Datasource]
   * Lấy script code trong divContentBk
   * @returns script code String
   */
  getScript(): string {
    let script: any;
    script = this.getDataBkSettingByKey(base._script);
    script = HELPER.formatCode(script);
    return script;
  }

  /**
   * [Get data from Datasource]
   * Lấy Template String từ divContentBk
   * @returns trả về chuỗi Template
   */
  getTemplate(): string {
    let template = this.getDataBkSettingByKey(base._template);
    if (template) {
      template = template.replace(/scri-pt/g, 'script');
    }
    template = HELPER.formatCode(template);
    return template;
  }
  /**
   * update seting field for this Atom
   */
  updateCurrentSettingField(key: string, data: any) {
    const atomData: AtomModel = data;
    if (atomData) {
      Object.defineProperty(this.data.atom, key, {
        value: data,
      });
    }
  }
  /**
   * updateInternalData
   */
  updateInternalData() {
    const dataSetting = DataManager.getDataSetting(this.getId());
    this.$el = dataSetting.$atom;
    this.data.atom = dataSetting.atom;

    return;
  }
  /**
   * setTemplate
   * @param template string
   */
  setTemplate(template: string): void {
    template = template.replace(/script/g, 'scri-pt');
    this.setDataBkSettingByKey(template, base._template);
    this.data.atom.html = template;
    this.render();
    return;
  }

  /**
   * [Get data from Datasource]
   * Lấy dánh mảng Libs từ divContentBk
   * @returns libs
   */
  getLibs() {
    let libs = this.getDataBkSettingByKey(base._libs);
    if (libs != undefined && libs.trim() != '') {
      let libJson: any = {};
      try {
        libJson = JSON.parse(libs.trim());
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log(err.message);
      }
      if (libJson['libs'] != undefined && libJson['libs'].length > 0) {
        libs = libJson['libs'];
      }
      return libs;
    }
  }

  /**
   * Phân loại Atom: Row, Element, Module
   * @returns Tên kiểu Atom
   */
  getType(): string {
    let atomType = 'element';
    if (this.$el.hasClass('gf_row')) {
      atomType = 'row';
    } else if (this.$el.hasClass('module-wrap')) {
      atomType = 'module';
    } else if (this.$el.hasClass('element-wrap')) {
      atomType = 'element';
    }
    if (this.data.snippet) {
      const $snippet = jQuery(this.data.snippet);
      const type = $snippet.attr('data-type');
      switch (type) {
        case 'module':
          atomType = 'module';
          break;
        case 'element':
          atomType = 'element';
          break;
        case 'row':
          atomType = 'row';
          break;
      }
    }
    return atomType;
  }

  /**
   * Lấy id của Atom hiện tại (đã được gắn vào attr html)
   * @returns ID
   */
  getId(): string {
    return this.$el.attr('data-id') || '';
  }

  /**
   * Sinh Id ngẫu nhiên từ HELPER
   * @returns ID
   */
  generateId(): string {
    let elementId = HELPER.MakeIdInsideList(this.$el, undefined, undefined);
    if (elementId == '') {
      elementId = this.$el.attr('data-id') || HELPER.MakeId().toString();
    }
    return elementId;
  }

  /**
   * [Get data from Datasource]
   * Lấy chuỗi ID đã được gắn tiền tố theo từng loại Atom (trong divContentBk)
   * @returns ID String
   */
  getIdBk(): string {
    let elementTypeBk = '';
    if (this.$el.hasClass('gf_row')) {
      elementTypeBk = '#row-';
    } else if (this.$el.hasClass('module-wrap')) {
      elementTypeBk = '#module-';
    } else if (this.$el.hasClass('element-wrap')) {
      elementTypeBk = '#element-';
    }
    const idBk = elementTypeBk + this.getId();
    return idBk;
  }

  /**
   * Lấy Id Atom đã được gắn tiền tố trong div gryffeditor (Live Page)
   * @returns id String
   */
  getIdNode(): string {
    let elementType = '';
    if (this.$el.hasClass('gf_row')) {
      elementType = '#r-';
    } else if (this.$el.hasClass('module-wrap')) {
      elementType = '#m-';
    } else if (this.$el.hasClass('element-wrap')) {
      elementType = '#e-';
    }
    const idNode = elementType + this.getId();
    return idNode;
  }

  /**
   * [Context Menu]
   * openEditAtomCSS
   * open setting CSS popup khi mở context menu
   * */
  openEditAtomCSS(): void {
    const _this = this;
    this.$divContentHidden.find('#md-editor').remove();
    this.$divContentHidden.append(this._mdEditor);
    this.$divContentHidden.find('[gf-tag="title"]').text(`Edit ${this.$el.attr('data-label')}`);

    const style = this.getStyle();
    const oldStyle = style;

    const $activeModal = jQuery('#md-editor');
    $activeModal.find('#txtCode').val(HELPER.formatCSS(style));

    const $cEditor = ace.edit('txtCode');
    $cEditor.setTheme('ace/theme/monokai');
    $cEditor.setAutoScrollEditorIntoView(true);
    $cEditor.getSession().setTabSize(2);
    $cEditor.getSession().setUseWrapMode(true);

    $cEditor.setHighlightActiveLine(false);
    $cEditor.setOption('maxLines', 27);
    $cEditor.setOption('minLines', 27);
    $cEditor.session.setMode('ace/mode/css');

    ($activeModal as any).gfModal({
      opacity: 0.4,
      bg: 'rgb(62, 62, 62)',
      zIndex: 9999999,
    });
    let mw = 1000;
    const windowOuterWidth = jQuery(window).outerWidth();
    if (windowOuterWidth && windowOuterWidth < 1000) {
      mw = windowOuterWidth;
    }
    $activeModal
      .data('gfmodal')
      .setTitle('CSS')
      .setWidth(mw + 'px')
      .centerModal()
      .show();

    $activeModal
      .find('.btnSave')
      .off()
      .on('click', function () {
        const style = $cEditor.getValue();
        _this.setStyle(style);

        HELPER.flagListItemChange(_this.$el, 'replace');
        HELPER.detectContentChange(_this.$el);

        $activeModal.data('gfmodal').hide();

        // History
        const extra = {
          edit: 'css',
          style: oldStyle,
        };

        const gfHistory = _this.$gryffEditor.data('gfhistory');
        if (gfHistory && _this.$el && _this.$el.attr('id') != undefined) {
          gfHistory.addHistory('editor', _this.$el.attr('id'), extra);
        }
        return false;
      });
    return;
  }

  /**
   * [Context Menu]
   * openEditAtomTemplate
   * open setting Template popup khi mở context menu
   * */
  openEditAtomTemplate(): void {
    this.$divContentHidden.find('#md-editor').remove();
    this.$divContentHidden.append(this._mdEditor);
    this.$divContentHidden.find('[gf-tag="title"]').text(`Edit ${this.getLabel()}`);

    const template = this.getTemplate();
    const oldTemplate = template;

    const $activeModal = this.$divContentHidden.find('#md-editor');

    // TODO: hook liquid thì cần format lại code template

    $activeModal.find('#txtCode').val(template);

    const $cEditor = ace.edit('txtCode');
    $cEditor.setTheme('ace/theme/monokai');
    $cEditor.setAutoScrollEditorIntoView(true);
    $cEditor.getSession().setTabSize(2);
    $cEditor.getSession().setUseWrapMode(true);

    $cEditor.setHighlightActiveLine(false);
    $cEditor.setOption('maxLines', 27);
    $cEditor.setOption('minLines', 27);
    $cEditor.session.setMode('ace/mode/html');

    ($activeModal as any).gfModal({
      opacity: 0.4,
      bg: 'rgb(62, 62, 62)',
      zIndex: 9999999,
    });

    const mw = (jQuery(window).outerWidth() || 0) < 1000 ? jQuery(window).outerWidth() : 1000;

    $activeModal
      .data('gfmodal')
      .setTitle('Template')
      .setWidth(mw + 'px')
      // .setHeight('500px')
      .centerModal() //
      .show();

    $activeModal
      .find('.btnSave')
      .off()
      .on('click', () => {
        const template = $cEditor.getValue();
        this.setTemplate(template);

        HELPER.flagListItemChange(this.$el, 'replace');
        HELPER.detectContentChange(this.$el);

        $activeModal.data('gfmodal').hide();

        // History
        const extra = {
          edit: 'html',
          template: oldTemplate,
        };
        if (this.$gryffEditor.data('gfhistory') && this.$el && this.$el.attr('id') != undefined) {
          this.$gryffEditor.data('gfhistory').addHistory('editor', this.$el.attr('id'), extra);
        }

        return false;
      });
  }

  /**
   * [Context Menu]
   * openAtomScript
   * open setting Script popup khi mở context menu
   */
  openEditAtomScript(): void {
    const _this = this;
    this.$divContentHidden.find('#md-editor').remove();
    this.$divContentHidden.append(this._mdEditor);
    this.$divContentHidden.find('[gf-tag="title"]').text(`Edit ${this.$el.attr('data-label')}`);

    const script = this.getScript();
    const oldScript = script;

    const $activeModal = jQuery('#md-editor');
    $activeModal.find('#txtCode').val(script);

    const $cEditor = ace.edit('txtCode');
    const scriptContent = $cEditor.getValue();
    // Ignore undifined case when missing <script></script> tag
    if (!scriptContent || scriptContent == 'undefined') {
      const scriptPlaceholder = '/* add your custom script here */';
      $cEditor.setValue(scriptPlaceholder);
    }
    $cEditor.setTheme('ace/theme/monokai');
    $cEditor.setAutoScrollEditorIntoView(true);
    $cEditor.getSession().setTabSize(2);
    $cEditor.getSession().setUseWrapMode(true);

    $cEditor.setHighlightActiveLine(false);
    $cEditor.setOption('maxLines', 27);
    $cEditor.setOption('minLines', 27);
    $cEditor.session.setMode('ace/mode/javascript');

    ($activeModal as any).gfModal({
      opacity: 0.4,
      bg: 'rgb(62, 62, 62)',
      zIndex: 9999999,
    });
    const mw = (jQuery(window) as any).outerWidth() < 1000 ? jQuery(window).outerWidth() : 1000;
    $activeModal
      .data('gfmodal')
      .setTitle('Script')
      .setWidth(mw + 'px')
      .centerModal()
      .show();

    $activeModal
      .find('.btnSave')
      .off()
      .on('click', function () {
        const script = $cEditor.getValue();
        _this.renderScript(script);

        HELPER.flagListItemChange(_this.$el, 'replace');
        HELPER.detectContentChange(_this.$el);
        $activeModal.data('gfmodal').hide();

        // History
        const extra = {
          edit: 'script',
          script: oldScript,
        };
        if (_this.$gryffEditor.data('gfhistory') && _this.$el && _this.$el.attr('id') != undefined) {
          _this.$gryffEditor.data('gfhistory').addHistory('editor', _this.$el.attr('id'), extra);
        }

        return false;
      });

    return;
  }

  /**
   * [Context Menu]
   * Vị trí xuất hiện Context Menu?
   * @returns true/false
   */
  atTheTop(): boolean {
    const $prevBlocks = this.$el.prevAll('.element-wrap[data-label], .module-wrap[data-label], .gf_row[data-label]');
    if ($prevBlocks.length > 0) {
      return false;
    }
    return true;
  }

  /**
   * [Context Menu]
   * Vị trí xuất hiện Context Menu?
   * @returns true/false
   */
  atTheBottom(): boolean {
    const $nextBlocks = this.$el.nextAll('.element-wrap[data-label], .module-wrap[data-label], .gf_row[data-label]');
    if ($nextBlocks.length > 0) {
      return false;
    }
    return true;
  }

  /**
   * [Context Menu]
   * Di chuyển Atom lên trên 1 đối tượng khác
   * @param scrollToMe có scroll đến vị trí mới hay không?
   */
  moveUp(scrollToMe: boolean): void {
    if (!this.atTheTop()) {
      if (scrollToMe == undefined) {
        scrollToMe = true;
      }

      // update place holder
      const $placeholder = this.$el.next('.block-placeholder');
      if ($placeholder.length > 0) {
        $placeholder.remove();
      }

      const $prevBlock = this.$el
        .prevAll('.element-wrap[data-label], .module-wrap[data-label], .gf_row[data-label]')
        .eq(0);
      let $newPlace = $prevBlock.prev('.block-placeholder');
      if ($newPlace.length == 0) {
        $newPlace = jQuery('<div class="block-placeholder" />');
        $prevBlock.before($newPlace);
      }

      // History
      let $itemContent;
      let position = {};
      if (this.$gryffEditor.data('gfhistory')) {
        position = this.$gryffEditor.data('gfhistory').getPosition($itemContent, this.$el);
      }

      const history = {
        id: this.$el.attr('id'),
        action: 'move',
        events: [
          {
            action: 'cut',
            position: position,
          },
        ],
      };
      this.$gryffEditor.trigger('history', history);

      const $from = this.$el.closest('.item-content');
      const $to = this.$el.closest('.item-content');

      let $aHandle = this.$el.children('.gf-tool-wrap').find('.e-handle');
      const type = this.getType();
      switch (type) {
        case 'row':
          $aHandle = this.$el.children('.gf-tool-wrap').find('.r-handle');
          break;
        case 'element':
          $aHandle = this.$el.children('.gf-tool-wrap').find('.e-handle');
          break;
        case 'module':
          $aHandle = this.$el.children('.gf-tool-wrap').find('.m-handle');
          break;
      }

      $aHandle.data('gfdraggable').manageStop($newPlace, this.$el, $from, $to);

      if (!HELPER.isInsideTheList(this.$el)) {
        if (scrollToMe) {
          const elOffset = this.$el.offset();
          if (elOffset) {
            this.$gryffEditor.parents('html, body').animate(
              {
                scrollTop: elOffset.top - 80,
              },
              800,
            );
          }
        }
      }
    }
  }

  /**
   * [Context Menu]
   * Di chuyển Atom xuống dưới 1 đối tượng khác
   * @param scrollToMe scroll đến vị trí mới hoặc không?
   */
  moveDown(scrollToMe: boolean): void {
    if (!this.atTheBottom()) {
      if (scrollToMe == undefined) {
        scrollToMe = true;
      }

      // update place holder
      const $placeholder = this.$el.next('.block-placeholder');
      if ($placeholder.length > 0) {
        $placeholder.remove();
      }

      const $nextBlock = this.$el
        .nextAll('.element-wrap[data-label], .module-wrap[data-label], .gf_row[data-label]')
        .eq(0);
      let $newPlace = $nextBlock.next('.block-placeholder');
      if ($newPlace.length == 0) {
        $newPlace = jQuery('<div class="block-placeholder" />');
        $nextBlock.after($newPlace);
      }

      // History
      let $itemContent;
      let position = {};
      if (this.$gryffEditor.data('gfhistory')) {
        position = this.$gryffEditor.data('gfhistory').getPosition($itemContent, this.$el);
      }

      const history = {
        id: this.$el.attr('id'),
        action: 'move',
        events: [
          {
            action: 'cut',
            position: position,
          },
        ],
      };
      this.$gryffEditor.trigger('history', history);

      const $from = this.$el.closest('.item-content');
      const $to = this.$el.closest('.item-content');

      let $aHandle = this.$el.children('.gf-tool-wrap').find('.e-handle');
      const type = this.getType();
      switch (type) {
        case 'row':
          $aHandle = this.$el.children('.gf-tool-wrap').find('.r-handle');
          break;
        case 'element':
          $aHandle = this.$el.children('.gf-tool-wrap').find('.e-handle');
          break;
        case 'module':
          $aHandle = this.$el.children('.gf-tool-wrap').find('.m-handle');
          break;
      }

      $aHandle.data('gfdraggable').manageStop($newPlace, this.$el, $from, $to);

      if (!HELPER.isInsideTheList(this.$el)) {
        if (scrollToMe) {
          const elOffset = this.$el.offset();
          if (elOffset) {
            this.$gryffEditor.parents('html, body').animate(
              {
                scrollTop: elOffset.top - 80,
              },
              800,
            );
          }
        }
      }
    }
  }

  /**
   * [Context Menu]
   * Xác định là Nested?
   * @returns true/false
   */
  hasParent(): boolean {
    const $parent = this.$el.parent().closest('.module-wrap[data-label], .gf_row[data-label]');
    if ($parent.length > 0) {
      const parentName = $parent.attr('data-label');
      let limitArr: string[] = [];
      if (parentName == 'Product') {
        limitArr = [
          '(P) Title',
          '(P) Price',
          '(P) Quantity',
          '(P) Image',
          '(P) Image List',
          '(P) Stock Counter',
          '(P) Cart Button',
          '(P) View More',
          '(P) Description',
          '(P) Variants',
          '(P) Swatches',
          '(P) Meta',
          'Infinite Options',
          'Recharge Subscription',
          'Bold Subscriptions',
          'Bold Product Options',
        ];
      } else if (parentName == 'Article List') {
        limitArr = ['(A) Title', '(A) Content', '(A) Image', '(A) Date'];
      } else {
        limitArr = [];
      }

      if (limitArr.length > 0) {
        const moduleName = this.getLabel();
        for (let i = 0; i < limitArr.length; i++) {
          if (moduleName == limitArr[i] || this.$el.find('.module-wrap[data-label="' + limitArr[i] + '"]').length > 0) {
            return false;
          }
        }
      }

      return true;
    }
    return false;
  }

  /**
   * [Context Menu]
   * toParent
   * @param scrollToMe scroll me
   */
  toParent(scrollToMe: boolean): void {
    if (this.hasParent()) {
      if (scrollToMe == undefined) {
        scrollToMe = true;
      }

      // update place holder
      const $placeholder = this.$el.next('.block-placeholder');
      if ($placeholder.length > 0) {
        $placeholder.remove();
      }

      const $parent = this.$el.parent().closest('.module-wrap[data-label], .gf_row[data-label]');
      let $newPlace = $parent.next('.block-placeholder');
      if ($newPlace.length == 0) {
        $newPlace = jQuery('<div class="block-placeholder" />');
        $parent.after($newPlace);
      }

      // History
      let $itemContent;
      let position = {};
      if (this.$gryffEditor.data('gfhistory')) {
        position = this.$gryffEditor.data('gfhistory').getPosition($itemContent, this.$el);
      }

      const history = {
        id: this.getIdNode(),
        action: 'move',
        events: [
          {
            action: 'cut',
            position: position,
          },
        ],
      };
      this.$gryffEditor.trigger('history', history);

      const $from = this.$el.closest('.item-content');
      const $to = this.$el.closest('.item-content');

      let $aHandle = this.$el.children('.gf-tool-wrap').find('.e-handle');
      const type = this.getType();
      switch (type) {
        case 'row':
          $aHandle = this.$el.children('.gf-tool-wrap').find('.r-handle');
          break;
        case 'element':
          $aHandle = this.$el.children('.gf-tool-wrap').find('.e-handle');
          break;
        case 'module':
          $aHandle = this.$el.children('.gf-tool-wrap').find('.m-handle');
          break;
      }

      $aHandle.data('gfdraggable').manageStop($newPlace, this.$el, $from, $to);

      if (!HELPER.isInsideTheList(this.$el)) {
        if (scrollToMe) {
          this.$gryffEditor.parents('html, body').animate(
            {
              scrollTop: (this.$el.offset()?.top || 0) - 80,
            },
            800,
          );
        }
      }
    }
  }

  /**
   * Append style
   * @param style string
   */
  runStyle(style?: string): void {
    if (style) {
      this.setStyle(style);
    }
    this.renderStyle();
  }

  /**
   * [Context Menu]
   * copyCSS
   */
  copyCSS(): void {
    const style = this.getStyle();
    const id = this.getId();
    const label = this.getModuleName();
    const dataClipBoardCSS = {
      id: id,
      label: label,
      style: style,
    };
    STORE.setValue('__clipBoardCSS', dataClipBoardCSS);
  }

  /**
   * [Context Menu]
   * pasteCSS
   */
  pasteCSS(): void {
    const clipBoardCSS = STORE.getValue('__clipBoardCSS') || {};
    const id = this.getId();
    const label = this.getModuleName();
    if (!jQuery.isEmptyObject(clipBoardCSS) && label == clipBoardCSS.label) {
      const oldStyle = this.getModuleCSS();
      let style = clipBoardCSS.style;
      if (id != clipBoardCSS.id) {
        const oldId = new RegExp(clipBoardCSS.id, 'g');
        style = style.replace(oldId, id);
      }
      this.setStyle(style);

      HELPER.flagListItemChange(this.$el, 'replace');
      HELPER.detectContentChange(this.$el);
      //add history
      const extra = {
        edit: 'css',
        style: oldStyle,
      };
      if (this.$gryffEditor.data('gfhistory') && this.$el && this.$el.attr('id') != undefined) {
        this.$gryffEditor.data('gfhistory').addHistory('editor', this.$el.attr('id'), extra);
      }
      const merId = this.$el.attr('id') || '';
      localStorage.setItem('pasteCSS', merId);
      this.openSettings();
    }
  }

  /**
   * detect when drag child in to a Module (Fix tạm thời vì đang phụ thuộc vào Helper)
   * @param index dfa
   * @param key afd
   * @param content adsf
   * @returns Object jsonData = {}
   */
  detectModuleChange(index: number, key: string, content: string): void {
    const moduleName = this.getLabel();

    switch (moduleName) {
      case '(P) Meta':
        // PRODUCT META --
        HELPER.detectProductMetaChange(this.$el, content);
        break;

      default: {
        let jsonData: any = {
          items: [],
        };
        const $bkItemContent = jQuery('<div/>').html(HELPER.clearExportedData(content));
        $bkItemContent.find('>.module-wrap').each(function () {
          const $that = jQuery(this);
          //   mId = $that.attr("data-id");
          $that.html('');
        });

        const moduleId = this.getId();
        content = $bkItemContent.html();

        // Remove unnecessary quote mark in background-image url to avoid bug
        $bkItemContent.find('.module-wrap[data-label="(P) Swatches"] .gf_swatch').each(function () {
          const bgImage = this.style.backgroundImage;
          const trimBgImage = bgImage.replace('url(', '').replace(')', '').replace(/"/g, '');

          content = content
            .replace('url("' + trimBgImage + '")', 'url(' + trimBgImage + ')')
            .replace('url(&quot;' + trimBgImage + '&quot;)', 'url(' + trimBgImage + ')');
        });

        try {
          jsonData = JSON.parse(getModuleData(moduleId));

          if (jsonData['items'][index] != undefined) {
            jsonData['items'][index][key] = content;

            const jsonHtml = JSON.stringify(jsonData, null, '\t');
            this.setDataBkSettingByKey(jsonHtml, base._data);
          }

          // PRODUCT LIST -- Product List / Related Products / Article List
          HELPER.detectListItemChange(this.$el);

          // PRODUCT META --
          if (this.$el.closest('[data-label="(P) Meta"]').length > 0) {
            const $productMeta = this.$el.closest('[data-label="(P) Meta"]');
            $productMeta.find('.item-content').each(function () {
              const $itemContent = jQuery(this);
              if ($itemContent.closest('.module-wrap').is($productMeta)) {
                HELPER.detectContentChange($itemContent);
              }
            });
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.log(err.message);
        }

        this.setDataChilds(jsonData);
        return jsonData;
      }
    }
  }
  /**
   * get Data from html attr
   * @returns Group Name
   */
  getGroupName(): string {
    return this.$el.attr('data-groupname') || '';
  }

  /**
   * override getLabel từ Atom
   * @returns Slug
   */
  getSlug(): string {
    return this.$el.attr('data-slug') || '';
  }
  /**
   * apply all event when user interactive in Live Page
   */
  applyEvents(): void {
    this.atomRender.applyEvents();
  }
  /*===========================================FIX TẠM THỜI========================================= */
  /**
   * Lấy tên class của Atom
   * @returns class name
   */
  getTargetClass(): string {
    const targetClass = this.data.atom.class || '';
    return targetClass;
  }
  /**
   * [fake function]
   * get element name
   * @returns string
   */
  getElementName(): string {
    return this.getLabel();
  }
  /**
   * [fake function]
   * get module name
   * @returns string
   */
  getModuleName(): string {
    return this.getLabel();
  }
  /**
   * [fake function]
   * get module CSS
   * @returns string
   * */
  getModuleCSS(): string {
    return this.getStyle();
  }
  /**
   * getTargetClass
   * @returns Class name of module
   */
  getTargetClassSelector(): string {
    return '';
  }
  /**
   * getTargetWrap
   * @returns Class name of module
   */
  getTargetWrapSelector(): string {
    return '';
  }
  /**
   * [fake function]
   * @param tab tab name
   * @param drag drag or click
   * @param capture save history
   */
  openRowSettings(tab: string | undefined, drag = false, capture?: boolean): void {
    this.openSettings(tab, drag, capture);
  }

  /**
   * [fake function]
   * @param tab tab name
   * @param drag drag or click
   * @param capture save history?
   */
  openElementSettings(tab: string | undefined, drag = false, capture?: boolean): void {
    this.openSettings(tab, drag, capture);
  }

  /**
   * [fake function]
   * @param tab tab name
   * @param drag drag or click
   * @param capture save history?
   */
  openModuleSettings(tab: string | undefined, drag = false, capture?: boolean): void {
    this.openSettings(tab, drag, capture);
  }

  /**
   * [fake function]
   * @returns style string
   */
  getElementCSS(): string {
    return this.getStyle();
  }
  /**
   * [fake function]
   * @param style style string
   * @returns void
   */
  setElementCSS(style: string): void {
    this.setStyle(style);
  }
  /**
   * [fake function]
   * @returns style string
   */
  setModuleCSS(): void {
    this.runStyle();
  }

  /**
   * [fake function]
   * get icon
   * @returns string
   *
   */
  getModuleIcon(): string {
    return this.getIcon();
  }

  /**
   * [STORE trigger]
   * Yêu cầu sự kiện showTool của Toolbar thông qua Store
   * @param force bool
   * @param enableTool default show or hide tool
   */
  showTool(force = false, enableTool = true): void {
    DataManager.setState('isShowToolbar', true, this.getId());
    DataManager.setState('showToolbar', { force: true, enableTool: true }, this.getId());
    DataManager.getInstance().notify();
    // STORE.events.trigger(`__showTool-${this.data.atom.id}`, {
    //   force,
    //   enableTool
    // });
  }

  /** [STORE trigger]
   * Yêu cầu sự kiện showTool từ Toolbar thông qua Store */
  hideTool(): void {
    DataManager.setState('isShowToolbar', false, this.getId());
    DataManager.getInstance().notify();
    // STORE.events.trigger(`__hideTool-${this.data.atom.id}`);
  }

  /**
   * [fake function]
   */
  moduleScript(): void {
    this.openEditAtomScript();
  }

  /**
   * [fake function]
   */
  moduleTemplate(): void {
    this.openEditAtomTemplate();
  }

  /**
   * [fake function]
   * */
  elementTemplate(): void {
    this.openEditAtomTemplate();
  }

  /**
   * [fake function]
   * */
  elementCSS(): void {
    this.openEditAtomCSS();
  }

  /**
   * [fake function]
   * */
  moduleCSS(): void {
    this.openEditAtomCSS();
  }

  /**
   * [fake function]
   * refresh
   */
  refresh(): void {
    this.render();
  }

  /**
   * ..
   * @param {Array} enableSections ..
   */
  editCode(enableSections: Array<string> = []): void {
    this.$divContentHidden.find('#md-editor').remove();
    this.$divContentHidden.append(this._mdEditor);
    this.$divContentHidden.find('[gf-tag="title"]').text(`Edit ${this.$el.attr('data-label')}`);
    const _this = this;
    const style = this.getStyle();
    const oldStyle = style;
    let template = this.getTemplate();
    const label = this.getLabel();
    const targetClass = this.getTargetClass();
    const istextField = targetClass == '.gf_textarea' || targetClass == '.gf_textfield';
    if (
      label == 'Heading' ||
      label == 'Text Block' ||
      label == 'Image' ||
      label == 'Button' ||
      label == 'Icon' ||
      istextField
    ) {
      template = this.$el.find('.elm').html();
    } else {
      template = _this.getTemplate();
    }

    const oldTemplate = template;

    const script = this.getScript();
    const oldScript = script;

    // liquid new version
    const atomName = this.getLabel();
    if (atomName == 'Liquid') {
      const $templateHtml = jQuery('<div />').html(template);
      if ($templateHtml.children('.module').length > 0) {
        template = $templateHtml.children('.module').html();
      }
    }

    const $activeModal = this.$divContentHidden.find('#md-editor');
    $activeModal.find('#templateCode').hide().val(template);
    $activeModal.find('#styleCode').hide().val(HELPER.formatCSS(style));
    $activeModal.find('#scriptCode').hide().val(script);
    $activeModal.find('#dataCode').hide();
    // $activeModal.find("#dataCode").hide().val(data);

    const $codeEditor: any = {
      template: null,
      style: null,
      script: null,
      // data: null,
    };

    // Đánh active cho tab đầu tiên không bị disable
    {
      const tab = enableSections[0];
      $activeModal.find(`[gf-tabs]>[gf-tab="${tab}"]`).addClass('active');
      $codeEditor[tab] = initAce(tab + 'Code', tab);
      if (tab == 'script') {
        const scriptContent = $codeEditor[tab].getValue();
        // Ignore undifined case when missing <script></script> tag
        if (!scriptContent || scriptContent == 'undefined') {
          const scriptPlaceholder = '/* add your custom script here */';
          $codeEditor[tab].setValue(scriptPlaceholder);
        }
      }
    }

    // huỷ trạng thái disable của những tab được active
    $activeModal.find('[gf-tabs]>[gf-tab]').each(function () {
      const $item = jQuery(this);
      const tab: string = ($item.attr('gf-tab') || '').toLowerCase();
      if (enableSections.includes(tab)) {
        $item.removeClass('disabled');
      }
    });

    ($activeModal as any).gfModal({
      opacity: 0.4,
      bg: 'rgb(62, 62, 62)',
      zIndex: 9999999,
    });

    let __timeout: any = -1;
    // xử lý click vào tag để copy id
    $activeModal
      .find('[gf-action="click:copyToClipboard"]')
      .off('click.clipboard')
      .on('click.clipboard', function () {
        const copied = copyToClipboard(_this.getIdNode());
        if (copied) {
          const $thisSpan = jQuery(this);
          $thisSpan.parent().find('[gf-span="msg"]').remove();
          clearTimeout(__timeout);
          const $msg = jQuery('<span gf-span="msg">Copied!</span>');
          $msg.css({
            color: '#42C184',
            'font-size': '12px',
            'margin-left': '4px',
          });
          $thisSpan.after($msg);
          __timeout = setTimeout(() => {
            $thisSpan.next('span').remove();
          }, 1500);
        }
      });

    const mw = (jQuery(window) as any).outerWidth() < 1000 ? (jQuery(window) as any).outerWidth() : 1000;

    $activeModal
      .data('gfmodal')
      .setTitle(_this.getIdNode() + oldUtils.svgIcon('copy'))
      .setWidth(mw + 'px')
      // .setHeight('500px')
      .centerModal() //
      .show();

    // xử lý chuyển tab
    $activeModal
      .find('[gf-tabs]>[gf-tab]:not(.disabled)')
      .off('click.changeTab')
      .on('click.changeTab', function () {
        const $selectedTab = jQuery(this);
        const tab = $selectedTab.attr('gf-tab') || '';
        if (!(enableSections as any).includes(tab)) {
          /* eslint no-console: ["error", { allow: ["warn", "error", "log"] }] */
          console.log(`The "${tab}" tab is disabled`);
          return;
        }
        $activeModal.find('[gf-tabs]>[gf-tab]').removeClass('active');
        $selectedTab.addClass('active');
        Object.keys($codeEditor).forEach((key) => {
          if ($codeEditor[key]) {
            jQuery($codeEditor[key].container).hide();
          }
        });
        // nếu đã được khởi tạo thì show, nếu chưa thì khởi tạo
        if ($codeEditor[tab]) {
          jQuery($codeEditor[tab].container).show();
        } else {
          $codeEditor[tab] = initAce(tab + 'Code', tab);
          if (tab == 'script') {
            const scriptContent = $codeEditor[tab].getValue();
            // Ignore undifined case when missing <script></script> tag
            if (!scriptContent || scriptContent == 'undefined') {
              const scriptPlaceholder = '/* add your custom script here */';
              $codeEditor[tab].setValue(scriptPlaceholder);
            }
          }
        }
      });
    // xử lý khi click lưu
    $activeModal
      .find('.btnSave')
      .off('click')
      .on('click', function () {
        enableSections.forEach((key) => {
          if (!$codeEditor[key]) {
            return;
          }
          const val = $codeEditor[key].getValue();
          if (key == 'template') {
            _this.setTemplate(val);
          } else if (key == 'style') {
            _this.runStyle(val);
          } else if (key == 'script') {
            _this.renderScript(val);
          }
          // else if (key == "data") {
          //   _this.setModuleData(val);
          // }
        });

        HELPER.flagListItemChange(_this.$el, 'replace');
        HELPER.detectContentChange(_this.$el);

        $activeModal.data('gfmodal').hide();

        // History
        const extra = {
          edit: 'code',
          template: oldTemplate,
          style: oldStyle,
          script: oldScript,
          // "data": oldData,
        };
        if (_this.$gryffEditor.data('gfhistory') && _this.$el && _this.$el.attr('id') != undefined) {
          _this.$gryffEditor.data('gfhistory').addHistory('editor', _this.$el.attr('id'), extra);
        }

        return false;
      });
  }

  /**
   * setDynamic
   * @param flag flag
   * @returns returns
   */
  setDynamic(flag = true) {
    let value: string;

    if (flag) {
      this.$el.attr('data-status', 'dynamic');
      this.$el
        .find(
          '[data-label="(P) Title"], [data-label="(P) Vendor"], [data-label="(P) SKU"], [data-label="(P) Description"], [data-label="(P) Price"], [data-label="(P) Cart Button"], [data-label="(P) View More"], [data-label="(P) Image"], [data-label="(P) Image List"], [data-label="(P) Quantity"], [data-label="(P) Variants"], [data-label="(P) Swatches"], [data-label="(P) Meta"]',
        )
        .attr('data-status', 'dynamic');

      value = '1';
    } else {
      this.$el.removeAttr('data-status');
      this.$el.find('[data-label="(P) Title"]').removeAttr('data-status');
      this.$el
        .find(
          '[data-label="(P) Title"], [data-label="(P) Vendor"], [data-label="(P) SKU"], [data-label="(P) Description"], [data-label="(P) Price"], [data-label="(P) Cart Button"], [data-label="(P) View More"], [data-label="(P) Image"], [data-label="(P) Image List"], [data-label="(P) Quantity"], [data-label="(P) Variants"], [data-label="(P) Swatches"], [data-label="(P) Meta"]',
        )
        .removeAttr('data-status');

      value = '0';
    }

    const $settings = this.getDataBkSettingByKey(base._snippet, this.getIdBk());
    let fields = [];
    try {
      fields = JSON.parse($settings);
      for (let i = 0; i < fields.length; i++) {
        if (fields[i].name == 'dynamic') {
          fields[i]['default_value'] = value;
        }
      }
      this.setDataBkSettingByKey(JSON.stringify(fields), base._settings);
    } catch (err) {
      console.log(err.message);
    }
    // Detect content change
    const $itemContent = this.$el.closest('.item-content');
    if ($itemContent.length > 0) {
      HELPER.detectContentChange($itemContent);
    }

    return false;
  }

  /**
   * initChilds
   */
  initChilds(): any {}

  /**
   * initChilds
   */
  afterRender(): any {}

  /**
   * initChilds
   * @param params params
   */
  renderSettings(...params: any[]): any {}

  /**
   * get "data-ver"
   * @returns version
   */
  getModuleVersion() {
    let version = this.$el.attr('data-ver') || 0;
    if (typeof version != 'number') {
      version = parseFloat(version);
    }
    return version;
  }

  /**
   * get "data-id"
   * @returns id
   */
  getModuleId() {
    let moduleId: string | number = HELPER.MakeIdInsideList(this.$el);
    if (moduleId == '') {
      moduleId = this.$el.attr('data-id') || HELPER.MakeId();
    }
    return moduleId;
  }

  /**
   * get "data-fixed"
   * @returns fixed version
   */
  getModuleFixVersion() {
    const $template = jQuery(this.getDataBkSettingByKey(base._template));
    let fixVersion = $template.attr('data-fixed') || 0;
    if (typeof fixVersion != 'number') {
      fixVersion = parseFloat(fixVersion);
    }
    return fixVersion;
  }

  /**
   * get "template" from bkContent
   * @returns template
   */
  getModuleTemplate() {
    let template = this.getDataBkSettingByKey(base._template);
    if (template) {
      template = template.replace(/scri-pt/g, 'script');
    } else {
      template = this.data.atom.html;
    }
    template = HELPER.formatCode(template);
    return template;
  }

  /**
   * get "template" from bkContent
   * @returns template
   */
  setModuleTemplate(template: string, noRefresh: boolean | undefined) {
    // normally "noRefresh" is undefinded
    const moduleName = this.getModuleName();
    const $template = this.getDataBkSettingByKey(base._template);
    // liquid module
    if (moduleName == 'Liquid') {
      const $templateHtml = jQuery('<div />').html($template);
      if ($templateHtml.children('.module').length > 0) {
        $templateHtml.children('.module').html(template);
        template = $templateHtml.html();
      }
    }
    // disable script tag in module template
    template = template.replace(/script/g, 'scri-pt');
    this.setDataBkSettingByKey(template, base._template);
    this.data.atom.html = template;

    if (!noRefresh) {
      this.refresh();
    }
    return this;
  }

  /**
   * setModuleFixVersion
   */
  setModuleFixVersion(version: number) {
    this.$el.attr('data-fixed', version);
    const $template = jQuery('<div />').html(this.getDataBkSettingByKey(base._template));
    $template.attr('data-fixed', version);
  }

  /**
   * getModuleKey
   */
  getModuleKey() {
    if (this.data.atom.slug) {
      return this.data.atom.slug;
    } else if (this.$el.attr('data-key')) {
      return this.$el.attr('data-key');
    } else {
      const name = this.getModuleName();
      return getKeyFromModuleName(name);
    }
  }

  /**
   * get DataBkSetting store vues
   * @returns  Data Bk Setting
   */
  getDataBkSettingByKey(keyType: string, idBk = ''): any {
    // idBk có dạng #module-xxxx
    if (!idBk) {
      idBk = this.getIdBk();
    }
    idBk = idBk.replace('#', '');
    const keySetting = `${keyType}-${idBk}`;
    const $dataBkSetting = (app as any).$store.getters['editor/GET_DATA_SETTING_BY_KEY'](keySetting);
    return $dataBkSetting;
  }

  /**
   * get DataBkSetting store vues
   * @returns  Data Bk Setting
   */
  setDataBkSettingByKey(data: any, keyType: string, idBk = ''): any {
    // idBk có dạng #module-xxxx
    if (!idBk) {
      idBk = this.getIdBk();
    }
    idBk = idBk.replace('#', '');

    const keySetting = `${keyType}-${idBk}`;
    (app as any).$store.commit('editor/SET_DATA_SETTINGS', {
      id: keySetting,
      value: data,
    });
    return false;
  }

  /**
   * removeDataBkSettings store vues
   * @returns
   */
  removeDataBkSettings(): any {
    removeElement(this.getId());
    const atomType = this.getTypeAtom();
    (app as any).$store.commit('editor/DELETE_DATA_SETTING_BY_KEY', {
      keyType: atomType,
      attrId: this.getId(),
      data: {
        [base._style]: true,
        [base._template]: true,
        [base._snippet]: true,
        [base._libs]: true,
        [base._data]: true,
        [base._script]: true,
        [base._settings]: true,
        [base._interactions]: true,
        [base._actions]: true,
        [base._actionsStyle]: true,
        [base._templateChildHtml]: true,
        [base._templateChildBkHtml]: true,
      },
    });
    emitter.emit('gem-meter:element-removed');

    return false;
  }

  /**
   * removeChildDataBkSetting store vues
   * @returns
   */
  removeChildDataBkSetting(keyType: any, attrId: any): any {
    (app as any).$store.commit('editor/DELETE_DATA_SETTING_BY_KEY', {
      keyType: keyType,
      attrId: attrId,
      data: {
        [base._style]: true,
        [base._template]: true,
        [base._snippet]: true,
        [base._libs]: true,
        [base._data]: true,
        [base._script]: true,
        [base._settings]: true,
        [base._interactions]: true,
        [base._actions]: true,
        [base._actionsStyle]: true,
        [base._templateChildHtml]: true,
        [base._templateChildBkHtml]: true,
      },
    });
    return false;
  }

  /**
   * get DataSnippet store vuex
   * @returns  Data Bk Setting
   */
  getDataSnippetByKey(keySnippet: any = ''): JQuery {
    if (!keySnippet) {
      keySnippet = this.getModuleKey();
    }
    const $dataSnippet = jQuery((app as any).$store.getters['editor/GET_SNIPPETS_BY_KEY'](keySnippet));
    return $dataSnippet;
  }

  /**
   * getTypeAtom
   * @returns elementType
   */
  getTypeAtom(): string {
    let atomType = '';
    if (this.$el.hasClass('gf_row')) {
      atomType = 'row';
    } else if (this.$el.hasClass('module-wrap')) {
      atomType = 'module';
    } else if (this.$el.hasClass('element-wrap')) {
      atomType = 'element';
    }
    return atomType;
  }

  /**
   * get StyleContainer
   * @returns jQuery
   */
  get$StyleContainer(): JQuery {
    const atomType = this.getTypeAtom();
    return this.$gfFrame.contents().find(`#${atomType}-style-container`);
  }

  /**
   * get ScriptContainer
   * @returns jQuery
   */
  get$ScriptContainer(): JQuery {
    const atomType = this.getTypeAtom();
    return this.$gfFrame.contents().find(`#${atomType}-script-container`);
  }
}
